import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { decodeUrlParam } from "../../utils/urlUtils";
import { useSelector } from "react-redux";
import moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ThemeButton from "../../components/CustomComponents/Buttons/ThemeButton/ThemeButton";
import StaffTabBar from "./staffTabBar";
import "./manageScheduleRoster.css";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const buildEmptyWeek = (weekStartDate) =>
  dayNames.map((day, idx) => ({
    dayName: day,
    rosterDate: moment(weekStartDate).add(idx, "days").format("YYYY-MM-DD"),
    startTime: "",
    endTime: "",
    breakMinutes: "",
    isLeave: false,
    isPaidLeave: false,
    leaveReason: "",
    payoutId: null,
    assignedDuty: "",
    plannedOvertimeMinutes: "",
    isBreakInclusive: false,
  }));

const toMonday = (dateLike) => {
  const m = moment(dateLike).startOf("day");
  const weekday = m.isoWeekday();
  return m.subtract(weekday - 1, "days");
};

const formatTimeForInput = (time) => {
  if (!time) return "";
  const parsed = moment(time, ["HH:mm", "HH:mm:ss", "h:mm A", "h:mm a"], true);
  return parsed.isValid() ? parsed.format("HH:mm") : "";
};

const dayNameFromDate = (dateStr) => moment(dateStr).format("dddd");

/** Empty / zero → "" so inputs can use placeholder "0" (user can type without deleting). */
const minutesInputValue = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "";
  return n;
};

const isPastRosterDate = (rosterDate) =>
  moment(rosterDate).startOf("day").isBefore(moment().startOf("day"));

const getShiftDurationLabel = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  const start = moment(startTime, ["HH:mm", "HH:mm:ss"], true);
  const end = moment(endTime, ["HH:mm", "HH:mm:ss"], true);
  if (!start.isValid() || !end.isValid()) return null;
  let mins = end.diff(start, "minutes");
  if (mins < 0) mins += 24 * 60;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0 && m <= 0) return null;
  return m ? `${h}h ${m}m` : `${h}h`;
};

function ManageScheduleRoster() {
  const { UserName: encodedUserName } = useParams();
  const UserName = decodeUrlParam(encodedUserName);
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [selectedUserName, setSelectedUserName] = useState(UserName || "");
  const [roleOptions, setRoleOptions] = useState([]);
  const [weekStartDate, setWeekStartDate] = useState(toMonday(new Date()).format("YYYY-MM-DD"));
  const [weekDays, setWeekDays] = useState(buildEmptyWeek(toMonday(new Date())));
  const [setDefaultWorkingHours, setSetDefaultWorkingHours] = useState(false);
  const [restaurantHoursLabel, setRestaurantHoursLabel] = useState("");
  const [copyingPreviousWeek, setCopyingPreviousWeek] = useState(false);
  const [autoCopyNextWeek, setAutoCopyNextWeek] = useState(false);
  const [savingAutoCopy, setSavingAutoCopy] = useState(false);

  const getFieldStyle = (isLeave, isReadOnly = false) => ({
    backgroundColor: isReadOnly
      ? isDarkMode
        ? "#1a2332"
        : "#f3f4f6"
      : isLeave
        ? isDarkMode
          ? "#111827"
          : "#eef2f7"
        : isDarkMode
          ? "#2d3748"
          : "#ffffff",
    color: isDarkMode ? "#f9fafb" : "#111827",
    borderColor: isDarkMode ? "#4b5563" : "#d1d5db",
    opacity: isReadOnly || isLeave ? 0.85 : 1,
    cursor: isReadOnly ? "not-allowed" : undefined,
  });

  const weekRangeLabel = useMemo(() => {
    const start = moment(weekStartDate);
    const end = start.clone().add(6, "days");
    return `${start.format("MMM D")} - ${end.format("MMM D")}`;
  }, [weekStartDate]);

  const previousWeekStartDate = useMemo(
    () => moment(weekStartDate).subtract(7, "days").format("YYYY-MM-DD"),
    [weekStartDate]
  );

  const previousWeekRangeLabel = useMemo(() => {
    const start = moment(previousWeekStartDate);
    const end = start.clone().add(6, "days");
    return `${start.format("MMM D")} - ${end.format("MMM D")}`;
  }, [previousWeekStartDate]);

  useEffect(() => {
    setSelectedUserName(UserName || "");
  }, [UserName]);

  const fetchStaffList = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: {
          CompID,
          SortBy: "UserName",
          SortOrder: "ASC",
          Search: "",
          PageSize: 500,
          PageNumber: 1,
        },
      });
      const data = response.data?.data || [];
      const list = Array.isArray(data) ? data.filter(Boolean) : [];
      setStaffList(list);
      if (!selectedUserName && !UserName && list.length > 0) {
        setSelectedUserName(list[0].userName);
      }
    } catch (err) {
      showErrorToast("Failed to fetch staff list");
    }
  }, [CompID, selectedUserName, UserName]);

  const fetchRoles = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: { CompID },
      });
      const roles = response.data?.data || response.data || [];
      const mapped = Array.isArray(roles)
        ? roles
            .filter(Boolean)
            .map((role) => ({
              id: role.comRolePermission_ID ?? role.ComRolePermission_ID ?? role.id ?? role.ID,
              title: role.r_Title || role.rTitle || role.title || role.Title || "",
            }))
            .filter((role) => role.title)
        : [];
      setRoleOptions(mapped);
    } catch (err) {
      setRoleOptions([]);
      showErrorToast("Failed to fetch roles");
    }
  }, [CompID]);

  useEffect(() => {
    fetchStaffList();
  }, [fetchStaffList]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const selectedStaffLabel = useMemo(() => {
    if (!selectedUserName) return "";
    const staff = staffList.find((s) => s.userName === selectedUserName);
    if (!staff) return selectedUserName;
    const label = `${staff.firstName || ""} ${staff.lastName || ""}`.trim();
    return label || selectedUserName;
  }, [selectedUserName, staffList]);

  const selectedStaffDefaultRole = useMemo(() => {
    if (!selectedUserName) return "";
    const staff = staffList.find((s) => s.userName === selectedUserName);
    if (!staff) return "";
    const staffRoleId = staff.comRolePermission_ID ?? staff.ComRolePermission_ID ?? null;
    if (staffRoleId === null || staffRoleId === undefined) return "";
    const matchedRole = roleOptions.find((r) => Number(r.id) === Number(staffRoleId));
    return matchedRole?.title || "";
  }, [selectedUserName, staffList, roleOptions]);

  const mapRosterRecordToDay = useCallback(
    (day, match) => {
      if (!match) {
        return {
          ...day,
          assignedDuty: day.assignedDuty || selectedStaffDefaultRole,
        };
      }
      return {
        ...day,
        startTime: formatTimeForInput(match.startTime || match.StartTime),
        endTime: formatTimeForInput(match.endTime || match.EndTime),
        breakStartTime: formatTimeForInput(match.breakStartTime || match.BreakStartTime),
        breakMinutes: minutesInputValue(match.breakMinutes ?? match.BreakMinutes),
        isLeave: !!(match.isLeave ?? match.IsLeave),
        isPaidLeave: !!(match.isPaidLeave ?? match.IsPaidLeave),
        leaveReason: match.leaveReason ?? match.LeaveReason ?? "",
        payoutId: match.payoutId ?? match.Payout_ID ?? match.payout_ID ?? null,
        assignedDuty: match.assignedDuty || match.AssignedDuty || selectedStaffDefaultRole,
        plannedOvertimeMinutes: minutesInputValue(
          match.plannedOvertimeMinutes ?? match.PlannedOvertimeMinutes
        ),
        isBreakInclusive: !!(match.isBreakInclusive ?? match.IsBreakInclusive),
      };
    },
    [selectedStaffDefaultRole]
  );

  const mapRosterDays = useCallback(
    (days, targetWeekStart = weekStartDate) => {
      const emptyWeek = buildEmptyWeek(targetWeekStart);
      return emptyWeek.map((d) => {
        const match = days.find((x) => moment(x.rosterDate || x.RosterDate).format("YYYY-MM-DD") === d.rosterDate);
        return mapRosterRecordToDay(d, match);
      });
    },
    [weekStartDate, mapRosterRecordToDay]
  );

  const fetchRosterDaysForWeek = useCallback(
    async (startDate) => {
      const response = await axiosInstance.get(APIs.GET_STAFF_ROSTER_WEEK, {
        params: {
          CompID,
          UserName: selectedUserName,
          WeekStartDate: startDate,
        },
      });
      const payload = response.data?.data;
      return {
        days: payload?.days || payload?.Days || [],
        autoCopyRosterNextWeek: !!(
          payload?.autoCopyRosterNextWeek ?? payload?.AutoCopyRosterNextWeek
        ),
        autoCopyUseDefaultHours: !!(
          payload?.autoCopyUseDefaultHours ?? payload?.AutoCopyUseDefaultHours
        ),
      };
    },
    [CompID, selectedUserName]
  );

  const fetchRosterWeek = useCallback(async () => {
    if (!CompID || !selectedUserName || !weekStartDate) return [];
    try {
      setLoading(true);
      const { days, autoCopyRosterNextWeek, autoCopyUseDefaultHours } = await fetchRosterDaysForWeek(weekStartDate);
      setAutoCopyNextWeek(autoCopyRosterNextWeek);
      setSetDefaultWorkingHours(autoCopyUseDefaultHours);
      const mapped = mapRosterDays(days);
      setWeekDays(mapped);
      return mapped;
    } catch (err) {
      const fallback = buildEmptyWeek(weekStartDate).map((day) => ({
        ...day,
        assignedDuty: selectedStaffDefaultRole,
      }));
      setWeekDays(fallback);
      setAutoCopyNextWeek(false);
      setSetDefaultWorkingHours(false);
      showErrorToast("Failed to fetch schedule week");
      return fallback;
    } finally {
      setLoading(false);
    }
  }, [CompID, selectedUserName, weekStartDate, selectedStaffDefaultRole, mapRosterDays, fetchRosterDaysForWeek]);

  useEffect(() => {
    fetchRosterWeek();
    setSetDefaultWorkingHours(false);
  }, [fetchRosterWeek]);

  useEffect(() => {
    if (!selectedStaffDefaultRole) return;
    setWeekDays((prev) =>
      prev.map((day) =>
        isPastRosterDate(day.rosterDate)
          ? day
          : {
              ...day,
              assignedDuty: day.assignedDuty || selectedStaffDefaultRole,
            }
      )
    );
  }, [selectedStaffDefaultRole]);

  const handleWeekStartChange = (value) => {
    const monday = toMonday(value).format("YYYY-MM-DD");
    setWeekStartDate(monday);
  };

  const shiftWeek = (deltaWeeks) => {
    setWeekStartDate(moment(weekStartDate).add(deltaWeeks, "weeks").format("YYYY-MM-DD"));
  };

  const scheduledDaysCount = useMemo(
    () =>
      weekDays.filter(
        (day) =>
          day.isLeave ||
          (day.startTime && day.endTime)
      ).length,
    [weekDays]
  );

  const handleDayChange = (index, key, value) => {
    const day = weekDays[index];
    if (day && isPastRosterDate(day.rosterDate)) return;
    if (day?.payoutId) return;
    setWeekDays((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item;
        const next = { ...item, [key]: value };
        if (key === "isLeave" && !value) {
          next.isPaidLeave = false;
          next.leaveReason = "";
        }
        return next;
      })
    );
  };

  const handleSetAllDay = (index) => {
    const day = weekDays[index];
    if (day && isPastRosterDate(day.rosterDate)) return;
    setWeekDays((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? {
              ...item,
              isLeave: false,
              startTime: "00:00",
              endTime: "23:59",
            }
          : item
      )
    );
  };

  const handleApplyToAllDays = (index) => {
    const source = weekDays[index];
    if (!source || isPastRosterDate(source.rosterDate)) return;
    setWeekDays((prev) =>
      prev.map((item) => {
        if (isPastRosterDate(item.rosterDate)) return item;
        return {
          ...item,
          isLeave: source.isLeave,
          isPaidLeave: source.isPaidLeave,
          leaveReason: source.leaveReason,
          startTime: source.startTime,
          endTime: source.endTime,
          breakStartTime: source.breakStartTime,
          breakMinutes: source.breakMinutes,
          assignedDuty: source.assignedDuty,
          plannedOvertimeMinutes: source.plannedOvertimeMinutes,
          isBreakInclusive: source.isBreakInclusive,
        };
      })
    );
  };

  const handleAutoCopyNextWeekToggle = async (checked) => {
    if (!CompID || !selectedUserName) {
      showErrorToast("Please select staff first.");
      return;
    }

    const previous = autoCopyNextWeek;
    setAutoCopyNextWeek(checked);
    setSavingAutoCopy(true);
    try {
      const response = await axiosInstance.put(APIs.SET_STAFF_ROSTER_AUTO_COPY, {
        CompID,
        UserName: selectedUserName,
        AutoCopyRosterNextWeek: checked,
        AutoCopyUseDefaultHours: setDefaultWorkingHours,
        UpdateBy: "BackOffice",
      });

      if (!response.data?.status) {
        setAutoCopyNextWeek(previous);
        showErrorToast(response.data?.message || "Failed to update auto-copy setting.");
        return;
      }

      const copyStatus =
        response.data?.data?.copyResult?.status ||
        response.data?.data?.CopyResult?.Status ||
        "";
      if (checked) {
        if (String(copyStatus).toLowerCase() === "copied") {
          showSuccessToast(
            setDefaultWorkingHours
              ? "Same schedule next week enabled. Next week filled from restaurant hours."
              : "Same schedule next week enabled. Next week was filled."
          );
        } else if (String(copyStatus).toLowerCase() === "skipped") {
          showSuccessToast("Same schedule next week enabled. Next week already had a schedule.");
        } else if (String(copyStatus).toLowerCase() === "nosource") {
          showSuccessToast(
            setDefaultWorkingHours
              ? "Same schedule next week enabled. Set restaurant hours in Settings first."
              : "Same schedule next week enabled. Save this week first so it can copy."
          );
        } else {
          showSuccessToast(response.data?.message || "Same schedule next week enabled.");
        }
      } else {
        showSuccessToast("Same schedule next week disabled.");
      }
    } catch (err) {
      setAutoCopyNextWeek(previous);
      showErrorToast(
        err.response?.data?.message || "Failed to update auto-copy setting."
      );
    } finally {
      setSavingAutoCopy(false);
    }
  };

  const persistDefaultHoursPreference = async (enabled) => {
    if (!CompID || !selectedUserName) return false;
    try {
      const response = await axiosInstance.put(APIs.SET_STAFF_ROSTER_AUTO_COPY, {
        CompID,
        UserName: selectedUserName,
        AutoCopyRosterNextWeek: autoCopyNextWeek,
        AutoCopyUseDefaultHours: enabled,
        UpdateBy: "BackOffice",
      });
      return !!response.data?.status;
    } catch {
      return false;
    }
  };

  const applyDefaultWorkingHours = async (enabled) => {
    if (!enabled) {
      setSetDefaultWorkingHours(false);
      const saved = await persistDefaultHoursPreference(false);
      if (!saved) {
        showErrorToast("Failed to save default hours preference.");
        setSetDefaultWorkingHours(true);
      }
      return;
    }
    if (!CompID || !selectedUserName) {
      showErrorToast("Please select staff first.");
      setSetDefaultWorkingHours(false);
      return;
    }

    setSetDefaultWorkingHours(true);

    try {
      const hoursByDayName = await loadDefaultHoursFromSources();
      if (Object.keys(hoursByDayName).length > 0) {
        applyHoursToWeekDays(hoursByDayName);
        const saved = await persistDefaultHoursPreference(true);
        if (!saved) {
          showErrorToast("Hours loaded, but failed to save default-hours preference for auto-renewal.");
          return;
        }
        showSuccessToast(
          autoCopyNextWeek
            ? "Default hours loaded (including empty past days) and saved for auto-renewal. Click Save to apply this week."
            : "Default working hours loaded, including empty past days. Click Save to apply."
        );
      } else {
        setSetDefaultWorkingHours(false);
        showErrorToast("No default hours found. Set restaurant business hours in Settings first.");
      }
    } catch (err) {
      setSetDefaultWorkingHours(false);
      showErrorToast(err?.message || "Failed to load default working hours.");
    }
  };

  const applyPreviousWeekSchedule = async () => {
    if (!CompID || !selectedUserName) {
      showErrorToast("Please select staff first.");
      return;
    }

    try {
      setCopyingPreviousWeek(true);
      const { days: previousDays } = await fetchRosterDaysForWeek(previousWeekStartDate);

      if (!previousDays.length) {
        showErrorToast("No schedule found for the previous week.");
        return;
      }

      const sortedPreviousDays = [...previousDays].sort(
        (a, b) =>
          moment(a.rosterDate || a.RosterDate).valueOf() - moment(b.rosterDate || b.RosterDate).valueOf()
      );

      setWeekDays((prev) =>
        prev.map((day, idx) => {
          if (isPastRosterDate(day.rosterDate)) return day;
          return mapRosterRecordToDay(day, sortedPreviousDays[idx]);
        })
      );
      setSetDefaultWorkingHours(false);
      showSuccessToast(`Previous week (${previousWeekRangeLabel}) loaded. Click Save to apply.`);
    } catch (err) {
      showErrorToast("Failed to load previous week schedule.");
    } finally {
      setCopyingPreviousWeek(false);
    }
  };

  const handleEnableAllDays = () => {
    setWeekDays((prev) =>
      prev.map((day) => (isPastRosterDate(day.rosterDate) ? day : { ...day, isLeave: false }))
    );
  };

  const handleDisableAllDays = () => {
    setWeekDays((prev) =>
      prev.map((day) => (isPastRosterDate(day.rosterDate) ? day : { ...day, isLeave: true }))
    );
  };

  const applyHoursToWeekDays = useCallback(
    (hoursByDayName) => {
      setWeekDays((prev) =>
        prev.map((day) => {
          const hasExistingSchedule =
            !!day.isLeave || !!(day.startTime && day.endTime);

          // Past / payout-locked: only fill when the day has no schedule yet
          // so the week pattern is complete and copyable for auto-renewal.
          if (day.payoutId) return day;
          if (isPastRosterDate(day.rosterDate) && hasExistingSchedule) return day;

          const hours =
            hoursByDayName[day.dayName] ||
            hoursByDayName[dayNameFromDate(day.rosterDate)];
          if (!hours) return { ...day, isLeave: false };
          return {
            ...day,
            isLeave: false,
            startTime: formatTimeForInput(hours.startTime),
            endTime: formatTimeForInput(hours.endTime),
            breakStartTime: formatTimeForInput(hours.breakStartTime),
            breakMinutes: minutesInputValue(hours.breakMinutes),
            isBreakInclusive: !!hours.isBreakInclusive,
          };
        })
      );
    },
    []
  );

  const loadDefaultHoursFromSources = useCallback(async () => {
    const hoursByDayName = {};

    try {
      const settingsResponse = await axiosInstance.get(APIs.GET_COMPANY_SETTINGS);
      const timings = settingsResponse.data?.data?.timings || settingsResponse.data?.timings || [];
      const restaurant = timings.find(
        (t) =>
          (t.type === "Restaurant" || t.type === "Resturent" || t.Type === "Restaurant" || t.Type === "Resturent") &&
          (t.localizeID === "en" || t.LocalizeID === "en" || !t.localizeID)
      );
      if (restaurant) {
        dayNames.forEach((day) => {
          const startTime =
            restaurant[`aT_${day}Opening`] ||
            restaurant[`AT_${day}Opening`] ||
            restaurant[`at_${day}Opening`];
          const endTime =
            restaurant[`aT_${day}Closing`] ||
            restaurant[`AT_${day}Closing`] ||
            restaurant[`at_${day}Closing`];
          if (startTime || endTime) {
            hoursByDayName[day] = { startTime, endTime, breakStartTime: "", breakMinutes: "", isBreakInclusive: false };
          }
        });
      }
    } catch {
      // No company hours available
    }

    const dayHours = Object.values(hoursByDayName)
      .map((h) => ({
        start: formatTimeForInput(h.startTime),
        end: formatTimeForInput(h.endTime),
      }))
      .filter((h) => h.start && h.end);

    if (dayHours.length > 0) {
      const unique = [...new Set(dayHours.map((h) => `${h.start}-${h.end}`))];
      if (unique.length === 1) {
        setRestaurantHoursLabel(`${dayHours[0].start} - ${dayHours[0].end}`);
      } else {
        const starts = dayHours.map((h) => h.start).sort();
        const ends = dayHours.map((h) => h.end).sort();
        setRestaurantHoursLabel(`${starts[0]} - ${ends[ends.length - 1]}`);
      }
    } else {
      setRestaurantHoursLabel("");
    }

    return hoursByDayName;
  }, []);

  useEffect(() => {
    if (!CompID) return;
    loadDefaultHoursFromSources();
  }, [CompID, loadDefaultHoursFromSources]);

  const defaultHoursToggleLabel = restaurantHoursLabel
    ? `Default hours (${restaurantHoursLabel})`
    : "Default hours";

  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify(weekDays || []);
  }, [weekDays]);

  useEffect(() => {
    if (loading) return;
    initialFormSnapshotRef.current = getFormSnapshot();
  }, [loading, weekStartDate, selectedUserName]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty =
    !loading &&
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const saveRosterWeek = async (exitAfterSave = false) => {
    if (!CompID || !selectedUserName) {
      showErrorToast("Please select staff first.");
      return false;
    }

    try {
      setSaving(true);
      const payload = {
        compID: CompID,
        userName: selectedUserName,
        weekStartDate,
        autoCopyRosterNextWeek: !!autoCopyNextWeek,
        autoCopyUseDefaultHours: !!setDefaultWorkingHours,
        days: weekDays.map((d) => ({
          rosterDate: d.rosterDate,
          startTime: d.startTime || null,
          endTime: d.endTime || null,
          breakStartTime: d.breakStartTime || null,
          breakMinutes: Number(d.breakMinutes || 0),
          isLeave: !!d.isLeave,
          isPaidLeave: d.isLeave ? !!d.isPaidLeave : false,
          leaveReason: d.isLeave && d.leaveReason?.trim() ? d.leaveReason.trim() : null,
          assignedDuty: d.isLeave ? null : selectedStaffDefaultRole || d.assignedDuty || null,
          plannedOvertimeMinutes: d.isLeave ? 0 : Number(d.plannedOvertimeMinutes || 0),
          isBreakInclusive: !!d.isBreakInclusive,
        })),
      };
      const response = await axiosInstance.post(APIs.UPSERT_STAFF_ROSTER_WEEK, payload);
      if (response.data?.status) {
        showSuccessToast("Schedule week updated successfully.");
        initialFormSnapshotRef.current = getFormSnapshot();
        if (exitAfterSave) {
          setTimeout(() => navigate("/staff/manageStaff"), 300);
        }
        return true;
      } else {
        showErrorToast(response.data?.message || "Failed to update schedule week.");
        return false;
      }
    } catch (err) {
      showErrorToast("Failed to save schedule week.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => saveRosterWeek(false),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  return (
    <div className={`schedule-roster-page ${isDarkMode ? "dark-mode" : ""}`}>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/staff/manageStaff">Manage Staff</Link>
            </li>
            <li className="breadcrumb-item active">Schedule</li>
          </ol>
          <h1 className="page-header mb-0">
            Schedule – {selectedStaffLabel || selectedUserName || UserName}
          </h1>
        </div>
        <div className="ms-auto d-flex align-items-center gap-2">
          <ThemeButton
            variant="secondary"
            icon="fa fa-save"
            onClick={() => saveRosterWeek(false)}
            disabled={saving || loading}
          >
            {saving ? "Saving..." : "Save"}
          </ThemeButton>
          <ThemeButton
            icon="fa fa-save"
            onClick={() => saveRosterWeek(true)}
            disabled={saving || loading}
          >
            {saving ? "Saving..." : "Save and Exit"}
          </ThemeButton>
        </div>
      </div>

      <div className="card border-0">
        <StaffTabBar
          activeTab="rosterSchedule"
          useTab="false"
          UserName={selectedUserName || UserName}
        />

        <div className="px-0 pb-3" style={{ position: "relative", zIndex: 5 }}>
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Weekly Schedule"
            totalCount={scheduledDaysCount}
            titleAddon={
              <span className="text-muted small ms-1">{weekRangeLabel}</span>
            }
            headerControls={
              <>
                <div className="schedule-roster-week-nav">
                  <button
                    type="button"
                    className="schedule-roster-week-nav__btn"
                    onClick={() => shiftWeek(-1)}
                    aria-label="Previous week"
                    title="Previous week"
                  >
                    <i className="fa fa-chevron-left" />
                  </button>
                  <input
                    type="date"
                    className="schedule-roster-week-nav__date"
                    value={weekStartDate}
                    onChange={(e) => handleWeekStartChange(e.target.value)}
                    title="Week start (Monday)"
                  />
                  <button
                    type="button"
                    className="schedule-roster-week-nav__btn"
                    onClick={() => shiftWeek(1)}
                    aria-label="Next week"
                    title="Next week"
                  >
                    <i className="fa fa-chevron-right" />
                  </button>
                </div>
                <div
                  className="schedule-roster-default-hours"
                  title="Loads restaurant business hours into this week, including empty past days (existing past schedules are kept). When Same schedule next week is also on, auto-renewal uses current restaurant hours."
                >
                  <span>{defaultHoursToggleLabel}</span>
                  <ToggleSwitch
                    isChecked={setDefaultWorkingHours}
                    onToggle={applyDefaultWorkingHours}
                    label=""
                    themeColor={themeColor}
                    disabled={loading || saving || !selectedUserName}
                  />
                </div>
                <div className="schedule-roster-default-hours" title="When on, next week is auto-filled. With Default hours on: uses current restaurant timings. Off: copies this week’s pattern.">
                  <span>Same schedule next week</span>
                  <ToggleSwitch
                    isChecked={autoCopyNextWeek}
                    onToggle={handleAutoCopyNextWeekToggle}
                    label=""
                    themeColor={themeColor}
                    disabled={loading || saving || savingAutoCopy || !selectedUserName}
                  />
                </div>
              </>
            }
            controlsRight={
              <div className="schedule-roster-actions">
                <ThemeButton
                  variant="ghost"
                  icon="fa fa-calendar-week"
                  onClick={applyPreviousWeekSchedule}
                  disabled={loading || saving || copyingPreviousWeek}
                >
                  {copyingPreviousWeek
                    ? "Loading..."
                    : `Copy ${previousWeekRangeLabel}`}
                </ThemeButton>
                <ThemeButton
                  icon="fa fa-check-double"
                  onClick={handleEnableAllDays}
                  disabled={loading || saving}
                >
                  Enable All
                </ThemeButton>
                <ThemeButton
                  variant="danger"
                  icon="fa fa-times-circle"
                  onClick={handleDisableAllDays}
                  disabled={loading || saving}
                >
                  Disable All
                </ThemeButton>
              </div>
            }
          >
            <GridSectionLoader loading={loading} minHeight="280px">
              <div className="d-flex flex-column gap-3 px-3 pb-3">
                {weekDays.map((day, idx) => {
                  const isPastDay = isPastRosterDate(day.rosterDate);
                  const isLockedByPayout = !!day.payoutId;
                  const fieldDisabled = isPastDay || isLockedByPayout;

                  return (
                    <div
                      key={`${day.rosterDate}-${day.dayName}`}
                      className={[
                        "schedule-roster-day",
                        isPastDay ? "is-past" : "",
                        day.isLeave ? "is-leave" : "",
                        isLockedByPayout ? "is-locked" : "",
                        !day.isLeave && day.startTime && day.endTime ? "is-scheduled" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{ "--roster-accent": themeColor }}
                    >
                      <div className="schedule-roster-day__header">
                        <div className="schedule-roster-day__title">
                          <span className="schedule-roster-day__dow">
                            {day.dayName.slice(0, 3).toUpperCase()}
                          </span>
                          <div className="schedule-roster-day__title-text">
                            <span className="schedule-roster-day__name">
                              {day.dayName}
                            </span>
                            <span className="schedule-roster-day__date">
                              {moment(day.rosterDate).format("D MMM YYYY")}
                            </span>
                          </div>
                          <div className="schedule-roster-day__badges">
                            {isPastDay && (
                              <span className="schedule-roster-day__badge schedule-roster-day__badge--past">
                                Past
                              </span>
                            )}
                            {day.isLeave && (
                              <span className="schedule-roster-day__badge schedule-roster-day__badge--leave">
                                Leave
                              </span>
                            )}
                            {isLockedByPayout && (
                              <span className="schedule-roster-day__badge schedule-roster-day__badge--locked">
                                In payout
                              </span>
                            )}
                            {!day.isLeave &&
                              getShiftDurationLabel(day.startTime, day.endTime) && (
                                <span className="schedule-roster-day__badge schedule-roster-day__badge--duration">
                                  {getShiftDurationLabel(day.startTime, day.endTime)}
                                </span>
                              )}
                          </div>
                        </div>
                        <div className="schedule-roster-day__header-actions">
                          {!isPastDay && !day.isLeave && (
                            <>
                              <button
                                type="button"
                                className="schedule-roster-day__chip-btn"
                                style={{ color: themeColor, borderColor: themeColor }}
                                onClick={() => handleSetAllDay(idx)}
                                disabled={fieldDisabled}
                                title="Set All Day (24hrs)"
                              >
                                <i className="fa fa-clock" />
                                24h
                              </button>
                              <button
                                type="button"
                                className="schedule-roster-day__chip-btn"
                                style={{ color: "#10b981", borderColor: "#10b981" }}
                                onClick={() => handleApplyToAllDays(idx)}
                                disabled={fieldDisabled}
                                title="Apply to All Days"
                              >
                                <i className="fa fa-copy" />
                                Apply
                              </button>
                            </>
                          )}
                          <div className="schedule-roster-day__leave-toggle">
                            <span>Leave</span>
                            <ToggleSwitch
                              isChecked={day.isLeave}
                              onToggle={(checked) =>
                                handleDayChange(idx, "isLeave", checked)
                              }
                              label=""
                              themeColor={themeColor}
                              disabled={fieldDisabled}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="schedule-roster-day__body">
                        {day.isLeave ? (
                          <div className="schedule-roster-day__leave-panel">
                            <div className="schedule-roster-day__paid-toggle">
                              <span>Paid leave</span>
                              <ToggleSwitch
                                isChecked={day.isPaidLeave}
                                onToggle={(checked) =>
                                  handleDayChange(idx, "isPaidLeave", checked)
                                }
                                label=""
                                themeColor={themeColor}
                                disabled={fieldDisabled}
                              />
                            </div>
                            <div className="schedule-roster-day__field schedule-roster-day__field--grow">
                              <label>Reason</label>
                              <input
                                type="text"
                                className="form-control"
                                maxLength={500}
                                placeholder="Optional — e.g. sick, annual leave"
                                value={day.leaveReason || ""}
                                disabled={fieldDisabled}
                                style={getFieldStyle(true, isPastDay)}
                                onChange={(e) =>
                                  handleDayChange(idx, "leaveReason", e.target.value)
                                }
                              />
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="schedule-roster-day__sections">
                              <section className="schedule-roster-day__section">
                                <div className="schedule-roster-day__section-head">
                                  <i className="fa fa-briefcase" aria-hidden="true" />
                                  <span>Shift</span>
                                </div>
                                <div className="schedule-roster-day__shift-row">
                                  <div className="schedule-roster-day__field">
                                    <label>Start</label>
                                    <input
                                      type="time"
                                      className="form-control"
                                      value={day.startTime || ""}
                                      disabled={fieldDisabled}
                                      style={getFieldStyle(false, isPastDay)}
                                      onChange={(e) =>
                                        handleDayChange(idx, "startTime", e.target.value)
                                      }
                                    />
                                  </div>
                                  <span className="schedule-roster-day__to" aria-hidden="true">
                                    to
                                  </span>
                                  <div className="schedule-roster-day__field">
                                    <label>End</label>
                                    <input
                                      type="time"
                                      className="form-control"
                                      value={day.endTime || ""}
                                      disabled={fieldDisabled}
                                      style={getFieldStyle(false, isPastDay)}
                                      onChange={(e) =>
                                        handleDayChange(idx, "endTime", e.target.value)
                                      }
                                    />
                                  </div>
                                </div>
                              </section>

                              <section className="schedule-roster-day__section">
                                <div className="schedule-roster-day__section-head">
                                  <i className="fa fa-coffee" aria-hidden="true" />
                                  <span>Break</span>
                                </div>
                                <div className="schedule-roster-day__shift-row">
                                  <div className="schedule-roster-day__field">
                                    <label>Start</label>
                                    <input
                                      type="time"
                                      className="form-control"
                                      disabled={fieldDisabled}
                                      value={day.breakStartTime || ""}
                                      style={getFieldStyle(false, isPastDay)}
                                      onChange={(e) =>
                                        handleDayChange(
                                          idx,
                                          "breakStartTime",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                  <span className="schedule-roster-day__to" aria-hidden="true">
                                    ·
                                  </span>
                                  <div className="schedule-roster-day__field">
                                    <label>Minutes</label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      min="0"
                                      placeholder="0"
                                      value={minutesInputValue(day.breakMinutes)}
                                      disabled={fieldDisabled}
                                      style={getFieldStyle(false, isPastDay)}
                                      onChange={(e) => {
                                        const raw = e.target.value;
                                        handleDayChange(
                                          idx,
                                          "breakMinutes",
                                          raw === "" ? "" : Math.max(0, Number(raw) || 0)
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                              </section>

                              <section className="schedule-roster-day__section">
                                <div className="schedule-roster-day__section-head">
                                  <i className="fa fa-bolt" aria-hidden="true" />
                                  <span>Overtime</span>
                                </div>
                                <div className="schedule-roster-day__field">
                                  <label>Planned (min)</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    min="0"
                                    placeholder="0"
                                    value={minutesInputValue(day.plannedOvertimeMinutes)}
                                    disabled={fieldDisabled}
                                    style={getFieldStyle(false, isPastDay)}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      handleDayChange(
                                        idx,
                                        "plannedOvertimeMinutes",
                                        raw === "" ? "" : Math.max(0, Number(raw) || 0)
                                      );
                                    }}
                                  />
                                </div>
                              </section>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </GridSectionLoader>
          </ModernGridToolbar>
        </div>
      </div>
    </div>
  );
}

export default ManageScheduleRoster;
